import classNames from 'classnames';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useGetLoggedInUser } from '@revelio/auth';
import { Callout, Spinner, Text } from '@revelio/ui';

import { countUserTurns } from './assistant-engine-core';
import { useGlobalAiCommandStore } from './global-ai-assistant-command-store';
import { useGlobalAIAssistant } from './global-ai-assistant-context';
import { GLOBAL_AI_ASSISTANT_GREETING } from './global-ai-assistant-copy';
import styles from './global-ai-assistant-drawer.module.css';
import {
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from './global-ai-assistant-engine-store';
import layoutStyles from './global-ai-chat-transcript-pane.module.css';
import {
  AssistantAvatar,
  TranscriptMessages,
} from './global-ai-transcript-messages';
import { useGlobalAiOfferWiring } from './use-global-ai-offer-wiring';

const noop = () => {
  // intentionally empty
};

// How far the user must scroll UP (px) during a followed action before we stop
// auto-following its streaming output. Compared against scrollTop, which only
// decreases on a real user scroll — content appended below never moves it — so
// this is chunk-size-independent: a large streamed block can't be mistaken for a
// scroll-away.
const ACTION_FOLLOW_USER_SCROLL_SLACK = 24;

type GlobalAiChatTranscriptPaneProps = {
  /**
   * Skin hosting the pane. Controls the transcript testid so existing
   * drawer selectors keep working; layout differences live in the parents.
   */
  variant: 'drawer' | 'homepage';
};

/**
 * The scrollable conversation area shared by the drawer and the assistant
 * homepage: empty-state greeting, transcript messages with offer wiring,
 * generation status / thinking spinner, engine error banner, and the
 * pin-question-to-top spacer machinery. Reads conversation state from the
 * assistant context and actions from the engine store; owns only per-skin
 * scroll/measure state.
 */
export function GlobalAiChatTranscriptPane({
  variant,
}: GlobalAiChatTranscriptPaneProps) {
  const {
    generationStatus,
    transcript,
    markNavConfirmResolved,
    appendTranscript,
  } = useGlobalAIAssistant();

  const {
    api: engineApi,
    error,
    reportOfferLaunchingKey,
  } = useGlobalAiEngineStore();
  const isTurnRunning = useGlobalAiEngineStore(selectIsTurnRunning);

  const isCommandInFlight = useGlobalAiCommandStore(
    (state) => state.isCommandInFlight
  );

  const { loggedInUser } = useGetLoggedInUser();
  const userTabs = loggedInUser.tabs ?? [];
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [transcriptScrollRoot, setTranscriptScrollRoot] =
    useState<HTMLDivElement | null>(null);
  const handleTranscriptScrollRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      setTranscriptScrollRoot((previousNode) => {
        return previousNode === node ? previousNode : node;
      });
    },
    []
  );

  // When a new prompt is submitted, pin it to the top of the transcript
  // with empty space below (so the incoming answer streams in underneath without
  // shoving the question off-screen). `lastUserMessageRef` anchors the scroll;
  // `bottomSpacerHeight` reserves the room needed for the question to reach the
  // top, shrinking as the answer fills the viewport.
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
  const [bottomSpacerHeight, setBottomSpacerHeight] = useState(0);
  const lastUserMessageId = useMemo(() => {
    for (let i = transcript.length - 1; i >= 0; i -= 1) {
      const message = transcript[i];
      if (message?.role === 'user') {
        return message.id;
      }
    }
    return undefined;
  }, [transcript]);
  const userTurnCount = countUserTurns(transcript);
  const prevUserTurnCountRef = useRef(userTurnCount);
  // Tracks the turn count the spacer was last measured against, so the measure
  // effect can tell a brand-new question (needs reserving) from ongoing token
  // streaming within the same turn (only needs re-measuring while the spacer is
  // still shrinking).
  const measuredTurnCountRef = useRef(userTurnCount);

  // On mount (drawer open / homepage conversation view entered), jump to the
  // latest messages. Deferred a frame so layout has settled and scrollHeight
  // is final.
  useEffect(() => {
    if (!transcriptScrollRoot) {
      return;
    }
    const raf = requestAnimationFrame(() => {
      transcriptScrollRoot.scrollTop = transcriptScrollRoot.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [transcriptScrollRoot]);

  // Reserve just enough space below the latest question so it can sit
  // at the top of the viewport. Measured before paint so the pin-to-top scroll
  // below lands correctly. Subtracting the current spacer from `scrollHeight`
  // recovers the real content height, so this converges without jitter.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure on any content/size change
  useLayoutEffect(() => {
    const isNewQuestion = userTurnCount !== measuredTurnCountRef.current;
    // Confine the (forced-synchronous) layout read to the brief reservation
    // window: a new question just arrived, or the spacer is still shrinking
    // toward zero. Once it has collapsed and no new question has come in, skip
    // the read entirely — otherwise a long streaming answer would force a reflow
    // on every token even though the spacer is already 0 (Greptile P2).
    if (!isNewQuestion && bottomSpacerHeight === 0) {
      return;
    }
    const root = scrollRef.current;
    const userNode = lastUserMessageRef.current;
    if (!root || !userNode) {
      // Nodes not mounted yet — leave `measuredTurnCountRef` untouched so a new
      // question is retried on the next commit rather than silently skipped.
      if (bottomSpacerHeight !== 0) {
        setBottomSpacerHeight(0);
      }
      return;
    }
    measuredTurnCountRef.current = userTurnCount;
    const questionTop =
      userNode.getBoundingClientRect().top -
      root.getBoundingClientRect().top +
      root.scrollTop;
    const realContentHeight = root.scrollHeight - bottomSpacerHeight;
    const contentBelowQuestion = realContentHeight - questionTop;
    const desired = Math.max(
      0,
      Math.round(root.clientHeight - contentBelowQuestion)
    );
    if (Math.abs(desired - bottomSpacerHeight) > 1) {
      setBottomSpacerHeight(desired);
    }
  }, [
    bottomSpacerHeight,
    lastUserMessageId,
    transcript,
    isTurnRunning,
    generationStatus,
    isCommandInFlight,
    reportOfferLaunchingKey,
    transcriptScrollRoot,
  ]);

  // On a NEW question (the user-turn count grew), scroll it to the top.
  // For the rest of the turn we leave the scroll position alone, so the answer
  // streams in below the pinned question rather than yanking the view down.
  // rAF defers the scroll until after the spacer above has been laid out.
  useEffect(() => {
    const grew = userTurnCount > prevUserTurnCountRef.current;
    prevUserTurnCountRef.current = userTurnCount;
    if (!grew) {
      return;
    }
    const id = requestAnimationFrame(() => {
      const root = scrollRef.current;
      const userNode = lastUserMessageRef.current;
      if (!root || !userNode) {
        return;
      }
      const top =
        userNode.getBoundingClientRect().top -
        root.getBoundingClientRect().top +
        root.scrollTop;
      root.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [userTurnCount]);

  // Follow a link-fired action to the bottom, starting the INSTANT the offer is
  // clicked. Clicking "build a custom dashboard" (or a nav/report offer) kicks
  // off work that first shows a status ("Opening custom page…"), then streams a
  // reasoning trace + grounded narration for many seconds — a build runs
  // 20-40s. All of that lands at the BOTTOM, so if the user is scrolled up
  // looking at the offer they just clicked, they see nothing until (much later)
  // the narration turn happens to scroll — the "long delay, basically
  // pointless" report. Arm on the click itself so the chat reveals + follows the
  // action immediately, and keep the newest output in view as it streams.
  //
  // Arming EXPLICITLY at the click (below, via `beginActionFollow`, threaded
  // into the offer handlers) — rather than inferring a "silent turn" from the
  // isTurnRunning/userTurnCount transition — means the reveal doesn't wait for
  // the turn to start, and it can't be fooled by however React batches a typed
  // question's state updates.
  const followActionRef = useRef(false);
  const followUserTurnCountRef = useRef(userTurnCount);
  // Last scrollTop we programmatically set. A user scroll-up drops scrollTop
  // below it; content appended below the viewport leaves it unchanged — that's
  // how we tell "user scrolled away" from "more streamed in" without guessing a
  // chunk-size threshold.
  const followLastTopRef = useRef(0);
  // Scroll synchronously (not via rAF): callers run in a click handler or an
  // effect that fires after the DOM is committed, so scrollHeight is already
  // current, and a synchronous set also works when rAF is paused
  // (backgrounded/occluded tab) rather than silently no-op'ing.
  const scrollTranscriptToBottom = useCallback(() => {
    const root = scrollRef.current;
    if (!root) {
      return;
    }
    root.scrollTop = root.scrollHeight;
    followLastTopRef.current = root.scrollTop;
  }, []);
  // Handed to the offer handlers: called the moment an offer that produces chat
  // output is clicked, so the reveal is immediate.
  const beginActionFollow = useCallback(() => {
    followActionRef.current = true;
    scrollTranscriptToBottom();
  }, [scrollTranscriptToBottom]);

  // Keep the followed action's newest output in view as it streams. Disarm when
  // the user types a new question (pin-to-top owns that scroll) or deliberately
  // scrolls up. The typed-question check runs on EVERY content change, so it
  // holds even if the user bubble and the running-flag flip land in separate
  // commits.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on any content change to follow the stream
  useEffect(() => {
    const questionGrew = userTurnCount > followUserTurnCountRef.current;
    followUserTurnCountRef.current = userTurnCount;
    if (questionGrew) {
      followActionRef.current = false;
      return;
    }
    const root = scrollRef.current;
    if (!followActionRef.current || !root) {
      return;
    }
    if (
      root.scrollTop <
      followLastTopRef.current - ACTION_FOLLOW_USER_SCROLL_SLACK
    ) {
      followActionRef.current = false;
      return;
    }
    scrollTranscriptToBottom();
  }, [
    isTurnRunning,
    userTurnCount,
    transcript,
    generationStatus,
    scrollTranscriptToBottom,
  ]);

  const offerWiring = useGlobalAiOfferWiring({
    transcript,
    isTurnRunning,
    isCommandInFlight,
    userTabs,
    navigateToMatch:
      engineApi?.navigateToMatch ??
      (() => Promise.resolve({ navigated: false })),
    startCustomPageCreate:
      engineApi?.startCustomPageCreate ??
      (() => Promise.resolve({ ok: false })),
    generateForBuildClick:
      engineApi?.generateForBuildClick ??
      (() => Promise.resolve({ page: null })),
    beginSeededBuildNarration:
      engineApi?.beginSeededBuildNarration ?? (() => () => {}),
    markNavConfirmResolved,
    appendTranscript,
    startAssistantTurn:
      engineApi?.startAssistantTurn ?? (() => Promise.resolve()),
    armNavResume: engineApi?.armNavResume ?? noop,
    armCreatePageResume: engineApi?.armCreatePageResume ?? noop,
    armBuildHere: engineApi?.armBuildHere ?? noop,
    beginActionFollow,
    switchGeographyTaxonomy:
      engineApi?.switchGeographyTaxonomy ?? (() => Promise.resolve()),
  });

  // While a reasoning trace is actively running it already shows the live
  // "Working for Xs" timer + a spinning current step, so the bottom-of-chat
  // status spinner underneath it is redundant — suppress it until the trace ends.
  const hasActiveReasoningTrace = transcript.some(
    (m) =>
      m.role === 'assistant' &&
      m.kind === 'reasoning_trace' &&
      m.trace.endedAt === undefined
  );

  const isHomepage = variant === 'homepage';

  // The scrollable content. On the homepage the scroll ROOT spans the full
  // page width (so the scrollbar sits at the page edge, ChatGPT-style,
  // instead of down the middle of a narrow centered box); the messages
  // themselves stay in a centered max-width column via `transcriptBody`'s
  // wrapper below. In the drawer the root is already narrow, so the body
  // renders directly.
  const transcriptBody = (
    <>
      {transcript.length === 0 && !isTurnRunning ? (
        <div className={layoutStyles.greeting}>
          <AssistantAvatar />
          <Text className={`${styles.textMuted} ${styles.bodyText}`}>
            {GLOBAL_AI_ASSISTANT_GREETING}
          </Text>
        </div>
      ) : null}
      <TranscriptMessages
        transcript={transcript}
        messagesById={offerWiring.messagesById}
        stampedOfferKindsByTargetId={offerWiring.stampedOfferKindsByTargetId}
        reportOfferLaunchingKey={reportOfferLaunchingKey}
        isTurnRunning={isTurnRunning}
        isCommandInFlight={isCommandInFlight}
        lastUserMessageId={lastUserMessageId}
        lastUserMessageRef={lastUserMessageRef}
        transcriptScrollRoot={transcriptScrollRoot}
        userTabs={userTabs}
        fireNavOffer={offerWiring.fireNavOffer}
        fireBuildOffer={offerWiring.fireBuildOffer}
        fireStandardPageOffer={offerWiring.fireStandardPageOffer}
        fireGeoTaxOffer={offerWiring.fireGeoTaxOffer}
        getStandardPageOfferState={offerWiring.getStandardPageOfferState}
        onReportOfferClick={({ messageId, offer, prompt }) => {
          // The report-builder launch for an inline `offer:report` narration
          // trigger — the only report-offer surface now that the card is gone.
          void engineApi?.handleReportOfferClick(offer, prompt, messageId);
        }}
      />
      {/* The reasoning trace already streams the live narration, so the
          bottom status spinner is redundant whenever a trace is active —
          suppress it (status AND the idle "Thinking…" below). It only shows
          for the rare turn that produces no trace. */}
      {generationStatus && !hasActiveReasoningTrace ? (
        <div className={layoutStyles.generationStatus}>
          <Spinner size="sm" />
          <Text
            fontSize="x-sm"
            className={`${styles.text} ${layoutStyles.statusText}`}
            data-testid="global-ai-assistant-generation-status"
          >
            {generationStatus}
          </Text>
        </div>
      ) : null}
      {isTurnRunning && !generationStatus && !hasActiveReasoningTrace && (
        <div className={layoutStyles.thinkingState}>
          <Spinner size="sm" />
          <Text
            fontSize="x-sm"
            className={`${styles.text} ${layoutStyles.thinkingText}`}
          >
            Thinking…
          </Text>
        </div>
      )}
      {error && (
        <Callout
          variant="error"
          className={classNames(
            styles.alert,
            styles.compactAlert,
            styles.compactAlertSpaced
          )}
          title="Something went wrong"
          description={error}
        />
      )}
      {/* Reserves room so the latest question can scroll to the top
          of the viewport; shrinks toward 0 as the answer fills the space. */}
      {bottomSpacerHeight > 0 ? (
        <div
          aria-hidden
          className={layoutStyles.bottomSpacer}
          style={
            {
              '--bottom-spacer-height': `${bottomSpacerHeight}px`,
            } as CSSProperties
          }
        />
      ) : null}
    </>
  );

  return (
    <div
      ref={handleTranscriptScrollRootRef}
      className={
        isHomepage
          ? classNames(
              layoutStyles.scrollArea,
              layoutStyles.homeScroll,
              styles.homePane
            )
          : classNames(layoutStyles.scrollArea, layoutStyles.drawerScroll)
      }
      data-testid={
        isHomepage
          ? 'assistant-home-transcript'
          : 'global-ai-assistant-transcript'
      }
    >
      {isHomepage ? (
        <div className={layoutStyles.homeColumn}>{transcriptBody}</div>
      ) : (
        transcriptBody
      )}
    </div>
  );
}
